import { useEffect, useState } from "react";
import {
  fetchZonesPOReports,
  fetchSubdivisionPOReports
} from "Service/ApiService";

/* ------------------ DEBOUNCE ------------------ */
const useDebounce = (value, delay = 600) => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);

  return debounced;
};

const CollectionTargetAchvReportController = () => {
  const user = JSON.parse(localStorage.getItem("user"));

  /* ------------------ STATE ------------------ */
  const [divisionOptions, setDivisionOptions] = useState([]);
  const [zoneOptions, setZoneOptions] = useState([]);

  const [loadingDivisions, setLoadingDivisions] = useState(false);
  const [loadingZones, setLoadingZones] = useState(false);

  const cloading = loadingDivisions || loadingZones;

  // ✅ ALWAYS ARRAYS
  const [filters, setFilters] = useState({
    division: [],
    zone: []
  });

  const debouncedFilters = useDebounce(filters, 500);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  /* ------------------ BODY SCROLL LOCK ------------------ */
  useEffect(() => {
    if (cloading) {
      const scrollBarWidth =
        window.innerWidth - document.documentElement.clientWidth;

      document.body.style.position = "fixed";
      document.body.style.top = `-${window.scrollY}px`;
      document.body.style.width = "100%";
      document.body.style.paddingRight = `${scrollBarWidth}px`;
    } else {
      const scrollY = document.body.style.top;

      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.style.paddingRight = "";

      window.scrollTo(0, parseInt(scrollY || "0") * -1);
    }
  }, [cloading]);

  /* ------------------ FILTER HANDLER ------------------ */
  const handleFilterChange = (field) => (value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: Array.isArray(value) ? value : []
    }));
  };

  /* ------------------ LOAD DROPDOWNS ------------------ */
  useEffect(() => {
    let alive = true;

    const loadDropdowns = async () => {
      try {
        setLoadingDivisions(true);
        setLoadingZones(true);

        /* 1️⃣ DIVISIONS */
        const divRes = await fetchSubdivisionPOReports(user.userCode);

        if (alive && divRes?.status && Array.isArray(divRes.divisions)) {

          const divisions = divRes.divisions.map((d) => ({
            label: `${d.divName?.trim()} (${d.divCode})`,   // ✅ visible name
            value: d.divCode,                              // ✅ unique key
            amount: d.amount                              // optional (for badge)
          }));

          setDivisionOptions(divisions);

        } else {
          setDivisionOptions([]);
        }

        setLoadingDivisions(false);

        /* 2️⃣ ZONES */
        // const zoneRes = await fetchZonesPOReports(user.userCode, "collection");
        // if (alive && zoneRes?.status && Array.isArray(zoneRes.zones)) {
        //   setZoneOptions(
        //     zoneRes.zones.map((z, i) => ({
        //       label: z.region?.trim() || "(NO REGION)",
        //       value: `${z.zoneCode}_${i}`,   // unique
        //       zoneCode: z.zoneCode
        //     }))
        //   );
        // } else {
        //   setZoneOptions([]);
        // }
        // setLoadingZones(false);

        /* 2️⃣ ZONES */
        const zoneRes = await fetchZonesPOReports(user.userCode, "collection");

        if (alive && zoneRes?.status && Array.isArray(zoneRes.zones)) {

          const cleaned = zoneRes.zones
            .map((z) => ({
              zoneCode: String(z.zoneCode || "").trim(),
              region: String(z.region || "")
                .replace(/[\r\n]+/g, "")     // ✅ remove CR/LF
                .trim(),
              amount: z.amount
            }))
            .filter(z => z.zoneCode && z.region);

          // ✅ Deduplicate by zoneCode + region
          const uniqueMap = new Map();
          cleaned.forEach(z => {
            const key = `${z.zoneCode}_${z.region}`;
            if (!uniqueMap.has(key)) {
              uniqueMap.set(key, z);
            }
          });

          const uniqueZones = Array.from(uniqueMap.values());

          setZoneOptions(
            uniqueZones.map((z) => ({
              label: `${z.region} (${z.zoneCode})`,
              value: z.zoneCode,          // ✅ stable value
              amount: z.amount
            }))
          );

        } else {
          setZoneOptions([]);
        }

        setLoadingZones(false);


      } catch (e) {
        console.error("Dropdown load error", e);
        setLoadingDivisions(false);
        setLoadingZones(false);
      }
    };

    loadDropdowns();
    return () => (alive = false);
  }, [user.userCode]);

  /* ------------------ AUTO SELECT DEFAULTS ------------------ */

  // ✅ Auto select ALL DIVISIONS
  useEffect(() => {
    if (divisionOptions.length && filters.division.length === 0) {
      setFilters((p) => ({
        ...p,
        division: divisionOptions
      }));
    }
  }, [divisionOptions]);

  // ✅ Auto select ALL ZONES
  useEffect(() => {
    if (zoneOptions.length && filters.zone.length === 0) {
      setFilters((p) => ({
        ...p,
        zone: zoneOptions
      }));
    }
  }, [zoneOptions]);

  const dropdownsLoaded =
    !loadingDivisions &&
    !loadingZones &&
    divisionOptions.length > 0 &&
    zoneOptions.length > 0;

  /* ------------------ INITIAL LOAD PLACEHOLDER ------------------ */
  useEffect(() => {
    if (
      initialLoadDone ||
      !dropdownsLoaded ||
      !filters.division.length ||
      !filters.zone.length
    ) {
      return;
    }

    console.log("✅ Dropdowns ready — Initial load placeholder");
    setInitialLoadDone(true);
  }, [
    dropdownsLoaded,
    filters.division.length,
    filters.zone.length
  ]);

  /* ------------------ USER CHANGE PLACEHOLDER ------------------ */
  useEffect(() => {
    if (!initialLoadDone) return;

    console.log("🔄 Filters changed (debounced)", debouncedFilters);

    // 👉 Future: API call here

  }, [debouncedFilters, initialLoadDone]);

  /* ------------------ RETURN ------------------ */
  return {
    cloading,
    divisionOptions,
    zoneOptions,
    filters,
    handleFilterChange
  };
};

export default CollectionTargetAchvReportController;
